import React from 'react';

import {
  HashRouter,
  Route,
} from 'react-router-dom';

import ProcessingIndicator from '../../Common/ProcessingIndicator';

import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';
import { getSubscriptionListPageBreadcrumb } from '../Subscription/SubscriptionListPage';

import PageHeader from '../../Common/PageHeader';

import PageNavigationLinks from '../../PageNavigationLinks';
import PageNavigationLink from '../../PageNavigationLink';

import SubscriptionDetailNewSubscriptionPanel from './SubscriptionDetailNewSubscriptionPanel';
import SubscriptionDetailPayorPanel from './SubscriptionDetailPayorPanel';
import SubscriptionDetailHistoryPanel from './SubscriptionDetailHistoryPanel';
import SubscriptionProcessPayment from './SubscriptionProcessPayment';
import SubscriptionRecurringPayments from './SubscriptionRecurringPayments';
import SubscriptionPaymentOptions from './SubscriptionPaymentOptions';
import featureFlags from '../../../featureFlags';
import { queryGetFacilitySettings, queryGetSubscription } from '../../../Queries';
import { getRedirectUrl } from '../../../Utils/envUrl';

const TabNameEnum = {
  General: "General",
  ParkerHistory: "Parker History",
  ProcessPayment: "Process Payment",
  RecurringPayments: "Recurring Payments",
  PaymentOptions: "Payment Options",
};

const subscriptionFields = [
  'id',
  {
    'billing_profile': [
      'validation_only',
    ]
  }
];

class SubscriptionDetailPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    lockedRoutePath: null,
    facilityId: null,
    subscriptionId: null,
    validationOnly: true,
    companyName: null,
    selectedTab: TabNameEnum.General,
    adminProcessPayment: null,
    recurringPayment: null,
    paymentOptions: null,
    taxCertificate: null,
    accountId: null,
  };
  


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.state.facilityId = urlSearchParams.get('facility');
    this.state.subscriptionId = urlSearchParams.get('subscription');
  }



  async componentDidMount() {
    const initializationTasks = [];

    if (window.location.href.indexOf("#/history") != -1) {
      this.setState({ selectedTab: TabNameEnum.ParkerHistory });
    }
    else if (window.location.href.indexOf("#/recurring-payments") != -1) {
      this.setState({ selectedTab: TabNameEnum.RecurringPayments });
    }
    const facilitySettingsFields = ['admin_process_payments', 'manage_recurring_payments', 'edit_payment_options'];

    if (this.state.facilityId !== null) {
      const response = await queryGetFacilitySettings({
        facility_id: this.state.facilityId
      }, facilitySettingsFields);

      if (response.errors) throw response.errors.map(error => error.message);
      const { manage_recurring_payments, admin_process_payments, edit_payment_options } = response.data.getFacilitySettings;
      this.setState({ adminProcessPayment: admin_process_payments, recurringPayment: manage_recurring_payments, paymentOptions: edit_payment_options });
    }

    if (this.state.subscriptionId) {
      initializationTasks.push(this.getValidationOnly());
    } else {
      this.setState({ validationOnly: false });
    }

    Promise.all(initializationTasks).then(() => {
      this.setState({
        pageInitializationComplete: true
      }, () => {
        if (!window.location.hash || window.location.hash === '#/') {
          window.location.hash = '#/payor';
        }
      });
    }, errors => {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors
      });
    });
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.subscriptionId !== prevState.subscriptionId) {
      if (this.state.subscriptionId) {
        await this.getValidationOnly();
      } else if (this.state.validationOnly) {
        this.setState({ validationOnly: false });
      }
    }
  }

  async getValidationOnly() {
    const response = await queryGetSubscription(
      { subscription_id: this.state.subscriptionId },
      subscriptionFields
    );
    if (response.errors) throw response.errors.map(error => error.message);
    const { validation_only } = response.data.getSubscription.billing_profile;
    this.setState({ validationOnly: validation_only, accountId: response.data.getSubscription.account_id });
  }

  setSubscriptionId = (subscriptionId) => {
    return new Promise((resolve/*, reject*/) => {
      const url = `${window.location.origin}` +
        `${window.location.pathname}` +
        `${window.location.search}` +
        `&subscription=${subscriptionId}` +
        `#/payor`;
      window.history.replaceState(null, null, url);

      this.setState({
        subscriptionId
      }, () => {
        resolve();
      });
    });
  }

  setCompanyName = (companyName) => {
    this.setState({ companyName });
  }


  lockRoute = (routePath) => {
    return new Promise((resolve/*, reject*/) => {
      this.setState({
        lockedRoutePath: routePath
      }, () => {
        resolve();
      });
    });
  }


  unlockRoute = (routePath) => {
    return new Promise((resolve, reject) => {
      if (this.state.lockedRoutePath !== routePath) {
        reject(`Route path '${routePath}' is not currently locked.`);
        return;
      }

      this.setState({
        lockedRoutePath: null
      }, () => {
        resolve();
      });
    });
  }

  renderNavigationBreadcrumbs() {
    const {
      facilityId
    } = this.state;

    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, facilityId));
    breadcrumbs.push(getSubscriptionListPageBreadcrumb(false, facilityId));
    breadcrumbs.push(getSubscriptionDetailPageBreadcrumb());
    if (featureFlags.FEATURE_UNIVERSAL_SEARCH) {
      breadcrumbs.push({ title: 'Universal Search', href: getRedirectUrl(window.origin) })
    }
    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const {
      subscriptionId,
      companyName,
      selectedTab
    } = this.state;

    let heading;
    if (selectedTab == TabNameEnum.General) {
      heading = `${(subscriptionId === null) ? 'Create' : 'Manage'} Subscription`;
    }
    else {
      const headingCompanyName = (companyName == null || typeof (companyName) == "undefined") ? "" : companyName + " ";
      heading = headingCompanyName + selectedTab;
    }

    return (
      <PageHeader heading={heading} />
    );
  }

  renderPageInitializationBlock() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!pageInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the page:</p>
              <ul>
                {pageInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }


  renderPageMainContent() {
    const {
      subscriptionId
    } = this.state;

    return (
      <React.Fragment>
        {(subscriptionId === null) &&
          this.renderNewSubscriptionPanel()
        }
        {(subscriptionId !== null) &&
          this.renderExistingSubscriptionPanels()
        }
      </React.Fragment>
    );
  }


  renderNewSubscriptionPanel() {
    const {
      facilityId
    } = this.state;

    return (
      <SubscriptionDetailNewSubscriptionPanel facilityId={facilityId}
        setSubscriptionId={this.setSubscriptionId}
      />
    );
  }


  renderExistingSubscriptionPanels() {
    const {
      subscriptionId,
      validationOnly,
      recurringPayment,
      paymentOptions
    } = this.state;
    return (
      <HashRouter>
        {this.renderPageNavigationLinks()}
        <Route path="/payor"
          render={props => <SubscriptionDetailPayorPanel
            {...props}
            subscriptionId={subscriptionId}
            accountId={this.state.accountId}
            validationOnly={validationOnly}
            setCompanyName={this.setCompanyName}
            routePath={'payor'}
            lockRoute={this.lockRoute}
            unlockRoute={this.unlockRoute} />
          }
        />
        <Route path="/history"
          render={props => <SubscriptionDetailHistoryPanel
            {...props}
            subscriptionId={subscriptionId}
            routePath={'history'}
            lockRoute={this.lockRoute}
            unlockRoute={this.unlockRoute} />
          }
        />

        <Route path="/process-payment"
          render={props => <SubscriptionProcessPayment
            {...props}
            subscriptionId={subscriptionId}
            routePath={'process-payment'}
            lockRoute={this.lockRoute}
            unlockRoute={this.unlockRoute} />
          }
        />

        <Route path="/recurring-payments"
          render={props => <SubscriptionRecurringPayments
            {...props}
            subscriptionId={subscriptionId}
            recurringPayment={recurringPayment}
            paymentOptions={paymentOptions}
            routePath={'recurring-payments'}
            lockRoute={this.lockRoute}
            unlockRoute={this.unlockRoute} />
          }
        />

        <Route path="/payment-options"
          render={props => <SubscriptionPaymentOptions
            {...props}
            subscriptionId={subscriptionId}
            paymentOptions={paymentOptions}
            routePath={'payment-options'}
            lockRoute={this.lockRoute}
            unlockRoute={this.unlockRoute} />
          }
        />
      </HashRouter>
    );
  }


  renderPageNavigationLinks() {
    const {
      lockedRoutePath,
      adminProcessPayment,
      recurringPayment,
      paymentOptions,
    } = this.state;

    return (
      <PageNavigationLinks>
        <PageNavigationLink label="General"
          path="/payor"
          disabled={(lockedRoutePath !== null && lockedRoutePath !== 'payor')}
          onClick={() => { this.setState({ selectedTab: TabNameEnum.General }) }}
        />
        <PageNavigationLink label="History"
          path="/history"
          disabled={(lockedRoutePath !== null && lockedRoutePath !== 'History')}
          onClick={() => { this.setState({ selectedTab: TabNameEnum.ParkerHistory }) }}
        />
        {adminProcessPayment !== "DO_NOT_DISPLAY" &&
          <PageNavigationLink label="Process Payment"
            path="/process-payment"
            disabled={(lockedRoutePath !== null && lockedRoutePath !== 'process-payment')}
            onClick={() => { this.setState({ selectedTab: TabNameEnum.ProcessPayment }) }}
          />
        }

        {recurringPayment !== "DO_NOT_DISPLAY" &&
          <PageNavigationLink label="Recurring Payments"
            path="/recurring-payments"
            disabled={(lockedRoutePath !== null && lockedRoutePath !== 'recurring-payments')}
            onClick={() => { this.setState({ selectedTab: TabNameEnum.RecurringPayments }) }}
          />
        }

        {paymentOptions !== "DO_NOT_DISPLAY" &&
          <PageNavigationLink label="Payment Options"
            path="/payment-options"
            disabled={(lockedRoutePath !== null && lockedRoutePath !== 'payment-options')}
            onClick={() => { this.setState({ selectedTab: TabNameEnum.PaymentOptions }) }}
          />
        }
      </PageNavigationLinks>
    );
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--subscription-detail">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        <div className="parkchirp--page-content">
          {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
            this.renderPageInitializationBlock()
          }
          {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
            this.renderPageMainContent()
          }
        </div>
      </div>
    );
  }
}
export default SubscriptionDetailPage;


export function getSubscriptionDetailPageBreadcrumb(currentPage = true, subscriptionId = null, facilityId = null) {
  const title = 'Subscription';
  const href = (currentPage) ? null :
    `?parkchirp_subscription${(subscriptionId !== null) ? `&subscription=${subscriptionId}` : ''}${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
